import React, { useState, useEffect, useRef, useCallback } from "react";
import '../../css/index.css';
import HttpService from "../../services/HttpService";
import Button from "../Tools/Components/Button";
import ReferenceOpt from "./Components/ReferentieOpt";
import Confirm from "../../Components/ConfirmDialogue";
import { useTranslation } from 'react-i18next';

const FactuurReferentiePagina = () => {
    const axiosRef = useRef(null);
    const [pageNr, setPageNr] = useState(1);
    const [entriesPerPage, setEntriesPerPage] = useState(25);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [referentieOpts, setReferentieOpts] = useState([]);
    const [searchString, setSearchString] = useState("");
    const [idToDelete, setIdToDelete] = useState(null);
    const [creating, setCreating] = useState(false);
    const { t } = useTranslation();

    // Fetch data when dependencies change
    const fetchData = useCallback(() => {
        if (!axiosRef.current) return;

        axiosRef.current.get(
            `${process.env.REACT_APP_INKOFACT_API_URL}reference/?entries=${entriesPerPage}&page=${pageNr}&searchString=${searchString}`
        )
        .then(response => {
            setReferentieOpts(response.data.collection);
            setHasNextPage(response.data.hasNextPage);
        })
        .catch(() => {
            setReferentieOpts([]);
        });
    }, [entriesPerPage, pageNr, searchString]);

    useEffect(() => {
        const httpService = HttpService;
        httpService.configure();
        axiosRef.current = httpService.getAxiosClient();
        fetchData();
    }, [fetchData]);

    // Handle page cycling
    const cyclePage = useCallback((nrToCycle = 0) => {
        setPageNr(prev => Math.max(prev + nrToCycle, 1));
    }, []);

    // Handle search changes
    const handleSearchChange = useCallback((searchString) => {
        setSearchString(searchString);
        setPageNr(1);
    }, []);

    // Handle entries per page changes
    const handleEntriesChange = useCallback((entries) => {
        setEntriesPerPage(entries);
    }, []);

    // Handle save operation
    const handleSave = useCallback((refOpt) => {
        if (!axiosRef.current) return;
        
        axiosRef.current.put(
            `${process.env.REACT_APP_INKOFACT_API_URL}reference`, 
            refOpt
        )
        .then(() => fetchData())
        .catch(ex => alert("Error: " + ex.message));
    }, [fetchData]);

    // Handle delete operation
    const handleDelete = useCallback((id) => {
        if (!axiosRef.current) return;

        axiosRef.current.delete(
            `${process.env.REACT_APP_INKOFACT_API_URL}reference/${id}`
        )
        .then(() => {
            const oldPageCount = Math.ceil(referentieOpts.length / entriesPerPage);
            const currentPageCount = Math.ceil((referentieOpts.length - 1) / entriesPerPage);
            
            if (oldPageCount === currentPageCount || pageNr <= currentPageCount) {
                fetchData();
            } else {
                setPageNr(1);
                fetchData();
            }
            setIdToDelete(null);
        })
        .catch(ex => alert("Error: " + ex.message));
    }, [entriesPerPage, fetchData, pageNr, referentieOpts.length]);

    // Handle create operation
    const handleCreate = useCallback((refOpt) => {
        if (!axiosRef.current) return;

        axiosRef.current.post(
            `${process.env.REACT_APP_INKOFACT_API_URL}reference/`, 
            refOpt
        )
        .then(() => {
            setPageNr(1);
            fetchData();
            setCreating(false);
        })
        .catch(ex => alert("Error: " + ex.message));
    }, [fetchData]);

    // Handle search input changes
    const onSearchChangeHandler = useCallback((e) => {
        handleSearchChange(e.target.value);
    }, [handleSearchChange]);

    // Handle entries per page changes
    const onEntriesChangeHandler = useCallback((e) => {
        handleEntriesChange(Number(e.target.value));
    }, [handleEntriesChange]);

    // Handle delete confirmation
    const onDeleteConfirm = useCallback(() => {
        if (idToDelete) handleDelete(idToDelete);
    }, [handleDelete, idToDelete]);

    // Handle delete cancel
    const onDeleteCancel = useCallback(() => {
        setIdToDelete(null);
    }, []);

    // Handle page button clicks
    const onPageButtonClick = useCallback((nrToCycle) => {
        cyclePage(nrToCycle);
    }, [cyclePage]);

    // Handle create button click
    const onCreateClick = useCallback(() => {
        setCreating(true);
    }, []);

    // Handle cancel create
    const onCancelCreate = useCallback(() => {
        setCreating(false);
    }, []);

    return (
        <div className="relative size-full" data-name="Content Box">
            <Confirm
                isOpen={idToDelete !== null}
                title={t("referentie.deleteTitle")}
                content={t("referentie.deleteMsg")}
                onConfirm={onDeleteConfirm}
                onCancel={onDeleteCancel}
            />
            <div className="flex flex-row items-start max-w-inherit min-w-inherit size-full gap-5">
                <div className="w-100 py-2">
                    <div className="dark:bg-[rgba(55,65,81,0.75)] content-fill flex flex-col items-center relative size-full">
                        <div
                            aria-hidden="true"
                            className="absolute inset-0 border-2 border-[#b0b6c1] pointer-events-none"
                        />
                        <div className="flex flex-col w-[364px]">
                            <div className="h-[80px] bg-[#00aaad] dark:bg-[#008b8e] border-2 border-[#b0b6c1] flex items-center justify-center">
                                <div className="capitalize font-bold text-[16px] tracking-[-0.08px]">
                                    {t("inkofactToolsBox.header")}
                                </div>
                            </div>
                            <div className="bg-[rgba(243,244,246,0.75)] border-2 border-t-0 border-[#b0b6c1] flex flex-col items-center gap-4 p-6">
                                <Button text={t("inkofactToolsBox.kvkBtn")} onClick="/FactuurKvk" />
                                <Button text={t("inkofactToolsBox.promptBtn")} onClick="/PromptOpt" />
                            </div>
                        </div>
                    </div>
                </div>
                <div className="flex flex-row items-start max-w-inherit min-w-inherit size-full gap-5">
                    <div className="box-border content-stretch flex flex-col items-center max-w-inherit min-w-inherit px-0 py-2 relative size-full">
                        <div className="bg-[#00aaad] dark:bg-[#008b8e] relative shrink-0 w-full" data-name="ContentBoxHeader">
                            <div aria-hidden="true" className="absolute border border-gray-400 border-solid inset-0 pointer-events-none" />
                            <div className="flex flex-col items-center justify-center size-full">
                                <div className="box-border content-stretch flex flex-col items-center justify-center px-[530px] py-[10px] relative w-full">
                                    <div className="py-3.5 capitalize flex flex-col font-['Inter:Bold',sans-serif] font-bold justify-center leading-[0] not-italic relative shrink-0 text-[20px] text-center tracking-[-0.1px] w-[300px]">
                                        <p className="leading-[1.45]">{t("referentie.header")}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="bg-[rgba(243,244,246,0.75)] dark:bg-[rgba(55,65,81,0.75)] relative shrink-0 w-full" data-name="MainContentFrame">
                            <div aria-hidden="true" className="absolute border border-gray-400 border-solid inset-0 pointer-events-none" />
                            <div className="flex flex-col items-center size-full">
                                <div className="box-border content-stretch flex flex-col gap-[10px] items-center px-[28px] py-[10px] relative w-full">
                                    <div className="content-stretch flex flex-col h-[23px] items-center justify-between relative shrink-0 w-full" data-name="Search Bar and button">
                                        <div className="absolute bottom-0 left-0 top-0">
                                            <div className="contents pointer-events-auto sticky top-0" data-name="Search Bar">
                                                <div className="absolute capitalize flex flex-col font-['Inter:Bold',sans-serif] font-bold justify-center leading-[0] left-[22px] not-italic text-[16px] text-center text-nowrap top-[11.5px] tracking-[-0.08px] translate-x-[-50%] translate-y-[-50%]">
                                                    <p className="leading-[2]">{t("referentie.searchLabel")}:</p>
                                                </div>
                                                <div className="absolute bg-white dark:bg-gray-700 h-[23px] left-[51px] top-0 w-[320px]">
                                                    <input 
                                                        className="dark:bg-gray-700 w-full h-full px-2 leading-[1.45]" 
                                                        onChange={onSearchChangeHandler} 
                                                        type="text" 
                                                        placeholder={t("referentie.receiver")} 
                                                    />
                                                    <div aria-hidden="true" className="absolute border border-black border-solid inset-0 pointer-events-none" />
                                                </div>
                                            </div>
                                        </div>
                                        <div className="absolute bottom-0 right-0 top-0">
                                            <Button text={t("referentie.newBtn")} onClick={onCreateClick} height="23" width="290" />
                                        </div>
                                    </div>
                                    <div className="content-stretch flex flex-col items-center justify-center relative shrink-0 w-full" style={{ textAlign: "left" }} data-name="Table">
                                        <div className="bg-[rgba(58,173,255,0.5)] dark:bg-[rgba(58,173,255,0.5)] h-[35px] relative shrink-0 w-full" data-name="TableHeader">
                                            <div aria-hidden="true" className="absolute border border-black border-solid inset-0 pointer-events-none" />
                                            <div className="flex flex-row items-center size-full">
                                                <div className="box-border capitalize content-stretch flex font-['Inter:Bold',sans-serif] font-bold h-[35px] items-center leading-[0] not-italic px-[10px] py-[6px] relative text-[16px] tracking-[-0.08px] w-full">
                                                    <div className="basis-0 flex flex-col grow h-full justify-center min-h-px min-w-px relative shrink-0">
                                                        <p className="leading-[1.45]">{t("referentie.receiver")}</p>
                                                    </div>
                                                    <div className="basis-0 flex flex-col grow h-full justify-center min-h-px min-w-px relative shrink-0">
                                                        <p className="leading-[1.45]">{t("referentie.regex")}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        {creating && (
                                            <ReferenceOpt 
                                                key={0} 
                                                referentieOpt={{ emailOntvanger: "", referenceRegex: "" }} 
                                                index={0} 
                                                onCancel={onCancelCreate} 
                                                editing={true} 
                                                onSave={handleCreate} 
                                            />
                                        )}
                                        {referentieOpts.map((referentieOpt, index) => (
                                            <ReferenceOpt 
                                                key={referentieOpt.id} 
                                                referentieOpt={referentieOpt} 
                                                index={index + 1} 
                                                onDelete={(id) => setIdToDelete(id)} 
                                                onSave={handleSave} 
                                            />
                                        ))}
                                    </div>
                                    {searchString === "" && (
                                        <div className="flex items-center gap-[10px]">
                                            <span className="text-sm font-medium">{t("referentie.pageSizeLabel")}:</span>
                                            <div className="w-[56px] h-[23px] relative" data-name="Page entries box">
                                                <div className="bg-white dark:bg-gray-700 border border-black rounded-sm w-full h-full flex items-center justify-center">
                                                    <input
                                                        id="entriesPerPage"
                                                        type="number"
                                                        onChange={onEntriesChangeHandler}
                                                        className="dark:bg-gray-700 w-[calc(100%-4px)] h-[calc(100%-4px)] text-center text-sm leading-[1.45] outline-none"
                                                        value={entriesPerPage}
                                                    />
                                                </div>
                                            </div>
                                            {pageNr > 1 && (
                                                <div className="w-[42px] h-[23px] relative" data-name="Page button">
                                                    <button 
                                                        className="bg-[#dedede] dark:bg-[#3F3F46] border-2 border-black rounded-full w-full h-full flex items-center justify-center" 
                                                        onClick={() => onPageButtonClick(-1)}
                                                    >
                                                        <span className="text-sm font-medium">{'<<'}</span>
                                                    </button>
                                                </div>
                                            )}
                                            {hasNextPage && (
                                                <div className="w-[42px] h-[23px] relative" data-name="Page button">
                                                    <button 
                                                        className="bg-[#dedede] dark:bg-[#3F3F46] border-2 border-black rounded-full w-full h-full flex items-center justify-center" 
                                                        onClick={() => onPageButtonClick(1)}
                                                    >
                                                        <span className="text-sm font-medium">{'>>'}</span>
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default FactuurReferentiePagina;
